import React, { useState, useEffect } from "react";
import {Menu, X} from "lucide-react";
import { useNavigate } from "react-router-dom";
import {ColorTheme} from "../../../themes/colors";
import {getNavigationStyles} from "../../../themes/styles/navigation";
import NavLink from "./NavLink";
import LanguageDropdown from "./LanguageDropdown";
import { useLanguage } from "../../../i18n/LanguageContext";

const APP_STORE_URL = "https://apps.apple.com/us/app/cook-ai-hey-chef/id6758898666";

interface NavigationProps {
    theme: ColorTheme;
    activeSection: string;
    isMenuOpen: boolean;
    isMobile: boolean;
    onMenuToggle: () => void;
    onNavigate: (section: string) => void;
}

const Navigation: React.FC<NavigationProps> = ({
                                                   theme,
                                                   activeSection,
                                                   isMenuOpen,
                                                   isMobile,
                                                   onMenuToggle,
                                                   onNavigate,
                                               }) => {
    const styles = getNavigationStyles(theme);
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [isScrolled, setIsScrolled] = useState(window.scrollY > 8);

    useEffect(() => {
        const onScroll = () => setIsScrolled(window.scrollY > 8);
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    return (
        <nav style={{
            ...styles.nav,
            background: isScrolled || isMenuOpen ? theme.navBackground : "transparent",
            borderBottom: isScrolled || isMenuOpen ? `1px solid ${theme.navBorder}` : "1px solid transparent",
            boxShadow: isScrolled ? "0 4px 24px rgba(7, 9, 26, 0.07)" : "none",
            backdropFilter: isScrolled || isMenuOpen ? "blur(14px)" : "none",
            WebkitBackdropFilter: isScrolled || isMenuOpen ? "blur(14px)" : "none",
            transition: "background 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease",
        }}>
            <div style={styles.navContainer}>
                <div style={styles.logo}>
                    <div style={{ ...styles.logoIcon, background: "transparent", padding: 0 }}>
                        <img
                            src="/images/cook_ai_logo.webp"
                            alt="Cook AI Logo"
                            style={{
                                width: "48px",
                                height: "48px",
                                objectFit: "contain",
                                borderRadius: "12px",
                            }}
                        />
                    </div>
                    <span style={styles.logoText}>Cook AI</span>
                </div>
                {/* Desktop Navigation */}
                <div style={!isMobile ? { ...styles.navLinks, alignItems: "center" } : {display: "none"}}>
                    <NavLink
                        href="#home"
                        isActive={activeSection === "home"}
                        onClick={() => onNavigate("home")}
                        theme={theme}
                    >
                        {t.nav.home}
                    </NavLink>
                    <NavLink
                        href="#features"
                        isActive={activeSection === "features"}
                        onClick={() => onNavigate("features")}
                        theme={theme}
                    >
                        {t.nav.features}
                    </NavLink>
                    <NavLink
                        href="/blog"
                        isActive={activeSection === "blog"}
                        onClick={() => navigate("/blog")}
                        theme={theme}
                    >
                        {t.nav.blog}
                    </NavLink>
                    <LanguageDropdown theme={theme} />
                    <a
                        href={APP_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            ...styles.downloadBtn,
                            textDecoration: "none",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                        }}
                        onMouseEnter={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.transform = "scale(1.05)";
                            (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0 10px 15px rgba(0, 0, 0, 0.2)";
                        }}
                        onMouseLeave={(e) => {
                            (e.currentTarget as HTMLAnchorElement).style.transform = "scale(1)";
                            (e.currentTarget as HTMLAnchorElement).style.boxShadow = "0 4px 6px rgba(0, 0, 0, 0.1)";
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 814 1000" fill="currentColor">
                            <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.9 0 130.1 46.4 173.4 46.4 42.7 0 109.2-49 188.3-49 30.5 0 108.2 2.6 160.6 61.4zm-244-190.5c31.5-37 53.5-88.1 53.5-139.2 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.5-55.1 135.2 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.1-71.1z"/>
                        </svg>
                        {t.nav.downloadApp}
                    </a>
                </div>

                {/* Mobile: Language dropdown + Menu Button */}
                <div style={isMobile ? { display: "flex", alignItems: "center", gap: "8px" } : { display: "none" }}>
                    <LanguageDropdown theme={theme} />
                    <button
                        onClick={onMenuToggle}
                        style={styles.mobileMenuBtn}
                        onMouseEnter={(e) => {
                            e.currentTarget.style.background = "#f3f4f6";
                        }}
                        onMouseLeave={(e) => {
                            e.currentTarget.style.background = "transparent";
                        }}
                    >
                        {isMenuOpen ? <X size={24}/> : <Menu size={24}/>}
                    </button>
                </div>
            </div>

            {/* Mobile Menu */}
            {isMenuOpen && (
                <div style={styles.mobileMenu}>
                    <NavLink
                        href="#home"
                        isActive={activeSection === "home"}
                        onClick={() => onNavigate("home")}
                        theme={theme}
                    >
                        {t.nav.home}
                    </NavLink>
                    <NavLink
                        href="#features"
                        isActive={activeSection === "features"}
                        onClick={() => onNavigate("features")}
                        theme={theme}
                    >
                        {t.nav.features}
                    </NavLink>
                    <NavLink
                        href="/blog"
                        isActive={activeSection === "blog"}
                        onClick={() => navigate("/blog")}
                        theme={theme}
                    >
                        {t.nav.blog}
                    </NavLink>
                    <a
                        href={APP_STORE_URL}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                            ...styles.downloadBtn,
                            width: "100%",
                            marginTop: "1rem",
                            marginLeft: 0,
                            textDecoration: "none",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            gap: "6px",
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 814 1000" fill="currentColor">
                            <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76 0-103.7 40.8-165.9 40.8s-105-37.5-155.5-127.4C46 790.7 0 663 0 541.8c0-207.5 135.4-317.3 269-317.3 70.9 0 130.1 46.4 173.4 46.4 42.7 0 109.2-49 188.3-49 30.5 0 108.2 2.6 160.6 61.4zm-244-190.5c31.5-37 53.5-88.1 53.5-139.2 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.5-55.1 135.2 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.1-71.1z"/>
                        </svg>
                        {t.nav.downloadApp}
                    </a>
                </div>
            )}
        </nav>
    );
};

export default Navigation;
